/**
 * AgentPicker — Dropdown selector for multiple agent configs
 *
 * Shows when the user clicks "AI" and multiple .agent.json
 * files exist in the workspace. Remembers last selection per session.
 */
import { useState, useEffect, useRef, useCallback, useLayoutEffect, type CSSProperties } from 'react'
import type { Workspace, FileStat } from '../engine/types'
import { DEFAULT_AGENT_CONFIG_JSON } from '../engine/defaultAgentConfig'
import './AgentPicker.css'

interface AgentPickerProps {
    workspace: Workspace
    anchorRect: { top: number; left: number }
    onSelect: (agentPath: string) => void
    onClose: () => void
}

interface AgentChoice {
    path: string
    name: string
    model: string
}

const SESSION_KEY = 'sb:lastAgent'

export function AgentPicker({ workspace, anchorRect, onSelect, onClose }: AgentPickerProps) {
    const [agents, setAgents] = useState<AgentChoice[]>([])
    const [pickerHeight, setPickerHeight] = useState(0)
    const ref = useRef<HTMLDivElement>(null)

    // Discover all .agent.json files
    useEffect(() => {
        const files = workspace.fs.list()
        const agentFiles = files.filter((f: FileStat) => f.path.endsWith('.agent.json'))

        Promise.all(
            agentFiles.map(async (f: FileStat) => {
                try {
                    const text = await workspace.fs.readText(f.path)
                    if (!text) return null
                    const parsed = JSON.parse(text)
                    return {
                        path: f.path,
                        name: parsed.name || f.path.split('/').pop()?.replace('.agent.json', '') || 'Agent',
                        model: parsed.model?.split('/').pop() || 'unknown',
                    } as AgentChoice
                } catch {
                    return null
                }
            }),
        ).then(results => {
            setAgents(results.filter((a): a is AgentChoice => a !== null))
        })
    }, [workspace])

    // Click outside handler
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [onClose])

    // ESC key handler
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [onClose])

    const handleSelect = useCallback((path: string) => {
        sessionStorage.setItem(SESSION_KEY, path)
        onSelect(path)
    }, [onSelect])

    useLayoutEffect(() => {
        if (!ref.current) return
        setPickerHeight(ref.current.offsetHeight)
    }, [agents])

    // Position near the trigger while keeping the picker fully visible.
    const PICKER_WIDTH = 252
    const PICKER_MAX_HEIGHT = 320
    const VIEWPORT_MARGIN = 8
    const BELOW_OFFSET = 30
    const ABOVE_OFFSET = 8

    const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchorRect.left - PICKER_WIDTH / 2, window.innerWidth - PICKER_WIDTH - VIEWPORT_MARGIN),
    )

    const showAbove = anchorRect.top > window.innerHeight / 2
    const effectiveHeight = pickerHeight || PICKER_MAX_HEIGHT
    const unclampedTop = showAbove
        ? anchorRect.top - effectiveHeight - ABOVE_OFFSET
        : anchorRect.top + BELOW_OFFSET
    const top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(unclampedTop, window.innerHeight - effectiveHeight - VIEWPORT_MARGIN),
    )

    const style: CSSProperties = {
        position: 'fixed',
        left,
        top,
    }

    if (agents.length === 0) return null

    const lastUsed = sessionStorage.getItem(SESSION_KEY)

    return (
        <div className="agent-picker" ref={ref} style={style}>
            <div className="agent-picker-title">Välj agent</div>
            {agents.map(agent => (
                <button
                    key={agent.path}
                    className={`agent-picker-item ${agent.path === lastUsed ? 'last-used' : ''}`}
                    onClick={() => handleSelect(agent.path)}
                >
                    <span className="agent-picker-avatar">🤖</span>
                    <div className="agent-picker-info">
                        <span className="agent-picker-name">{agent.name}</span>
                        <span className="agent-picker-model">{agent.model}</span>
                    </div>
                    {agent.path === lastUsed && <span className="agent-picker-badge">Senast</span>}
                </button>
            ))}
        </div>
    )
}

/**
 * Auto-select or show picker.
 * Returns the agent path immediately if only one exists,
 * or null (to signal the picker should be shown).
 */
export function getAutoSelectAgent(workspace: Workspace): string | null {
    const files = workspace.fs.list()
    const agentFiles = files.filter((f: FileStat) => f.path.endsWith('.agent.json'))

    if (agentFiles.length === 0) return null
    if (agentFiles.length === 1) return agentFiles[0].path

    // Multiple agents — check session preference or auto-select first
    const lastUsed = sessionStorage.getItem(SESSION_KEY)
    if (lastUsed && agentFiles.some(f => f.path === lastUsed)) {
        return lastUsed
    }

    // Auto-select first agent (better UX than interrupting with a picker mid-flow)
    return agentFiles[0].path
}

/**
 * Scan for existing .agent.json files, or auto-create a default agent config.
 * Returns the agent path, or null if the workspace is read-only.
 */
export function ensureAgentConfig(workspace: Workspace): string | null {
    const files = workspace.fs.list()
    const agentFiles = files.filter((f: FileStat) => f.path.endsWith('.agent.json'))
    if (agentFiles.length > 0) return agentFiles[0].path

    // Read-only workspaces can't create files — bail out
    if (!workspace.canWrite) return null

    // Auto-create default with spending-limited demo key
    const defaultPath = '/assistant.agent.json'
    workspace.fs.writeText(defaultPath, DEFAULT_AGENT_CONFIG_JSON)
    return defaultPath
}
